import { useState, useEffect } from 'react';
import { X, Lightbulb, CheckCircle, AlertCircle, Info } from 'lucide-react';

interface Notification {
  id: string;
  type: 'suggestion' | 'success' | 'error' | 'info';
  title: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationSystemProps {
  uiMode: 'invisible' | 'ambient' | 'explicit';
  suggestions: any[];
}

export function NotificationSystem({ uiMode, suggestions }: NotificationSystemProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    // Add suggestions as notifications
    if (suggestions.length > 0 && uiMode !== 'invisible') {
      suggestions.forEach(suggestion => {
        addNotification({
          type: 'suggestion',
          title: 'Smart Suggestion',
          message: suggestion.message || 'I noticed you might benefit from this...',
          action: suggestion.action,
        });
      });
    }
  }, [suggestions, uiMode]);

  const addNotification = (notification: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substring(7);
    setNotifications(prev => [...prev, { ...notification, id }]);
    
    // Auto-dismiss after 15 seconds to give plenty of time to read
    setTimeout(() => {
      removeNotification(id);
    }, 15000);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  if (uiMode === 'invisible' || notifications.length === 0) {
    return null;
  }

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'suggestion': return Lightbulb;
      case 'success': return CheckCircle;
      case 'error': return AlertCircle;
      case 'info': return Info;
    }
  };

  const getColors = (type: Notification['type']) => {
    switch (type) {
      case 'suggestion': return 'bg-purple-50 border-purple-200 text-purple-900';
      case 'success': return 'bg-green-50 border-green-200 text-green-900';
      case 'error': return 'bg-red-50 border-red-200 text-red-900';
      case 'info': return 'bg-blue-50 border-blue-200 text-blue-900';
    }
  };

  return (
    <div className={`
      fixed z-50 flex flex-col gap-3
      ${uiMode === 'ambient' 
        ? 'bottom-4 right-20' 
        : 'top-20 right-4'
      }
    `}>
      {notifications.map((notification) => {
        const Icon = getIcon(notification.type);
        
        return (
          <div
            key={notification.id}
            className={`
              ${getColors(notification.type)}
              border rounded-xl shadow-lg p-4 max-w-sm
              animate-slide-in-right
              backdrop-blur-sm
            `}
          >
            <div className="flex items-start gap-3">
              <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1">{notification.title}</h4>
                <p className="text-sm opacity-90">{notification.message}</p>
                {notification.action && (
                  <button
                    onClick={() => {
                      notification.action!.onClick();
                      removeNotification(notification.id);
                    }}
                    className="mt-2 text-sm font-medium underline hover:no-underline"
                  >
                    {notification.action.label}
                  </button>
                )}
              </div>
              <button
                onClick={() => removeNotification(notification.id)}
                className="p-1 hover:bg-khiimori-bg-elevated rounded transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}